import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { env } from './env';

// Lazy initialization to avoid TDZ errors
let _supabase: SupabaseClient | null = null;
let _initializationError: Error | null = null;

const getSupabase = (): SupabaseClient => {
  if (_initializationError) {
    throw _initializationError;
  }
  
  if (!_supabase) {
    try {
      const supabaseUrl = env.VITE_SUPABASE_URL;
      const supabaseAnonKey = env.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseAnonKey) {
        const error = new Error('Missing Supabase environment variables');
        // Store error details for error boundary
        (error as any).isSetupError = true;
        (error as any).missingEnvVars = [
          !supabaseUrl && 'VITE_SUPABASE_URL',
          !supabaseAnonKey && 'VITE_SUPABASE_ANON_KEY',
        ].filter(Boolean);
        _initializationError = error;
        throw error;
      }

      // Suppress Supabase LockManager warnings (browser compatibility issue)
      const originalConsoleWarn = console.warn;
      console.warn = (...args: any[]) => {
        // Filter out LockManager warnings from Supabase
        const message = args[0]?.toString() || '';
        if (message.includes('LockManager') || message.includes('@supabase/gotrue-js')) {
          return; // Suppress these warnings
        }
        originalConsoleWarn.apply(console, args);
      };

      _supabase = createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
          autoRefreshToken: true,
          persistSession: true,
          detectSessionInUrl: true,
          flowType: 'pkce', // Use PKCE flow for better security
        },
        realtime: {
          // Disable Realtime to prevent WebSocket connection errors
          // Enable when Realtime tables are properly configured
          params: {
            eventsPerSecond: 0,
          },
        },
        global: {
          headers: {
            'X-Client-Info': 'cyclone-platform',
          },
        },
      });
    } catch (error) {
      _initializationError = error instanceof Error ? error : new Error(String(error));
      throw _initializationError;
    }
  }
  
  return _supabase;
};

// Export lazy Proxy to avoid TDZ errors
export const supabase = new Proxy({} as SupabaseClient, {
  get(_target, prop) {
    const instance = getSupabase();
    const value = (instance as any)[prop];
    if (typeof value === 'function') {
      return value.bind(instance);
    }
    return value;
  },
  set(_target, prop, value) {
    const instance = getSupabase();
    (instance as any)[prop] = value;
    return true;
  },
  has(_target, prop) {
    const instance = getSupabase();
    return prop in instance;
  },
});

export type { User } from '@supabase/supabase-js';
